import React, { FC, useState, useContext } from 'react';
import injectSheet from 'react-jss';
import More from 'Assets/ui/more.svg';
import { SearchBar } from 'Components/SearchBar';
import Menu from 'Components/Menu';
import { numberWithCommas } from 'Utils/numberWithCommas';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { useRouter } from 'Utils/router';
import { Button } from '../../Buttons';
import styleSheet from './styles';

interface Props {
  onSearch?: (search: string) => void;
  addToListClicked?: () => void;
  removeFromListClicked?: () => void;
  exportButtonClicked?: () => void;
  exportDisabled?: boolean;
  exportButtonTitle?: string;
  audienceCountText?: string;
  selectedText?: string;
  listName?: string;
  audienceTotal?: number;
  checkedBoxCount?: number;
  classes?: any;
  items?: any;
  onMenuChange?: (event: any) => void;
}

const TableHeader: FC<Props> = (props: Props) => {
  const {
    classes,
    onSearch,
    addToListClicked,
    removeFromListClicked,
    exportButtonClicked,
    exportDisabled,
    exportButtonTitle,
    audienceCountText,
    selectedText,
    listName,
    audienceTotal,
    checkedBoxCount,
    onMenuChange,
    items,
  } = props;

  const [search, setSearch] = useState('');
  const [prevSearch, setPrevSearch] = useState('');
  const [error, setError] = useState('');
  const {
    helpers: {
      View,
      metrics: { store: metricsStore },
    },
    actions: { track },
  } = useContext(ProductAnalyticsContext);

  const { location } = useRouter();
  const addToListCallBack = () => {
    const { listTotalCount }: any = metricsStore;
    if (addToListClicked) addToListClicked();

    // Combined Analytics event
    track('add_to_list_started', {
      screen: View(location.pathname),
      scope:
        checkedBoxCount === audienceTotal || checkedBoxCount === 0
          ? 'All'
          : 'Selected',
      existing_list_count: listTotalCount, // This is the number of lists created by a user
    });
  };

  const remove =
    items && items.filter((item: any) => item.label.toLowerCase() === 'delete');

  const indicatorStyles = {
    height: '28px',
    width: '28px',
    borderRadius: '4px',
    backgroundColor: 'rgba(5, 29, 57, 0.1)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const onSearchUpdate = (e: any) => {
    // onSearch && onSearch(e.target.value.length >= 3 ? e.target.value : '');
    if (onSearch && e.target.value.length >= 3) onSearch(e.target.value);
    else onSearch('');

    setPrevSearch(e.target.value.length >= 3 ? e.target.value : '');
    setError(
      e.target.value.length > 0 &&
        e.target.value.length < 3 &&
        '[at least 3 characters required to search]'
    );
  };
  return (
    <>
      {listName && (
        <div
          className={classes.row}
          style={{ zIndex: 15, marginBottom: '18px' }}
        >
          <div className={classes.column}>
            <div className={classes.row}>
              <div className={`${classes.column}`}>
                <div style={{ wordBreak: 'break-all' }}>
                  <span
                    className={`${classes.listName} ${classes.baseText} ${classes.mediumText}`}
                  >
                    {listName}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className={classes.column}>
            <div
              className={classes.exportWrapper}
              style={{ cursor: 'pointer' }}
            >
              <Menu
                items={items}
                width="217px"
                indicator={More}
                fill="#085FE5"
                indicatorStyles={indicatorStyles}
                onChange={onMenuChange}
                ariaLabel="Table Header Menu"
              />
            </div>
          </div>
        </div>
      )}
      <div className={classes.row} style={{ zIndex: 1, userSelect: 'none' }}>
        <div className={classes.column}>
          <div className={classes.row}>
            <div className={`${classes.column}`}>
              <div>
                {checkedBoxCount === 0 ? (
                  <span
                    aria-live={
                      // Turn off aria-live if error exists, to avoid this message overwriting error reading.
                      error ? 'off' : 'polite'
                    }
                    aria-atomic="true"
                  >
                    <span
                      className={`${classes.audienceTotal} ${classes.baseText} ${classes.boldText}`}
                    >
                      {numberWithCommas(audienceTotal)}
                    </span>
                    <span
                      className={`${classes.audienceTotalText} ${classes.baseText}  ${classes.mediumText}`}
                    >
                      {audienceCountText}
                    </span>
                  </span>
                ) : (
                  <span aria-live="polite" aria-atomic="true">
                    <span
                      className={`${classes.checkedCountText} ${classes.baseText} ${classes.mediumText}`}
                    >
                      {checkedBoxCount}
                    </span>
                    <span
                      className={`${classes.audienceTotalText} ${classes.baseText}  ${classes.mediumText}`}
                    >
                      {selectedText}
                    </span>
                  </span>
                )}
                <SearchBar
                  disableInputOnly={audienceTotal === 0}
                  disableToolTip
                  disabled={false}
                  onChange={(event: any) => {
                    setSearch(event.target.value);
                    setError('');
                  }}
                  name="Inquiries Search"
                  placeholder="Search by Name"
                  width={190}
                  fields={['Name']}
                  searchable={
                    (search.length >= 3 && prevSearch !== search && true) ||
                    false
                  }
                  onSearch={onSearchUpdate}
                  error={error}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={classes.column}>
          <div className={classes.exportWrapper}>
            {listName && checkedBoxCount > 0 && remove && remove.length > 0 && (
              <Button onClick={removeFromListClicked} disabled={exportDisabled}>
                Remove From List
              </Button>
            )}
            <Button
              onClick={addToListCallBack}
              disabled={exportDisabled || audienceTotal < 1}
            >
              Add to List
            </Button>
            <Button
              onClick={exportButtonClicked}
              disabled={exportDisabled || audienceTotal < 1}
            >
              {exportButtonTitle}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

TableHeader.defaultProps = {
  classes: {},
  listName: '',
  audienceTotal: null,
  checkedBoxCount: null,
  onSearch: null,
  addToListClicked: null,
  removeFromListClicked: null,
  exportButtonClicked: null,
  selectedText: 'Selected',
  items: [],
  onMenuChange: null,
  audienceCountText: 'Inquiries',
  exportButtonTitle: 'Download',
  exportDisabled: false,
};

export default injectSheet(styleSheet)(TableHeader);
